import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtNumber, fmtDate } from "@/lib/format";
import { productTypeLabel, aggregateRequirements, planCode } from "@/lib/production";
import { convertQty, convertQtyOr } from "@/lib/units";
import { buildRequirementHtml } from "@/lib/requirementDoc";
import { SubAssembliesPanel, ExplodePanel } from "@/components/production/PlanBom";
import { toast } from "sonner";
import {
  ArrowLeft, Lock, Unlock, Play, PackagePlus, ShieldAlert, Loader2,
  AlertTriangle, Printer, Mail, ClipboardList, Tag, Search, Trash2, CheckCircle2,
  FileText, FileSpreadsheet,
} from "lucide-react";

const TABS = [
  { key: "products", label: "Products" },
  { key: "requirement", label: "Requirement" },
  { key: "subassemblies", label: "Sub-Assemblies" },
  { key: "explode", label: "Explode to Raw Materials" },
  { key: "production", label: "Production" },
  { key: "report", label: "Report" },
];


function StatBox({ label, value, tone = "text-stone-900", testId }) {  return (
    <div data-testid={testId} className="rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function ProductionPlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isSuperAdmin = role === "super_admin";

  const [plan, setPlan] = useState(null);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [tab, setTab] = useState("products");
  const [prodSearch, setProdSearch] = useState("");
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [prodMeta, setProdMeta] = useState({}); // productId -> {batchNo, mfgDate, expiryDate}
  const [earmark, setEarmark] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actuals, setActuals] = useState({});
  const [actualDate, setActualDate] = useState("");
  const [savingActual, setSavingActual] = useState(false);
  const [subAssy, setSubAssy] = useState(null);
  const [explosion, setExplosion] = useState(null);
  const [explodeExporting, setExplodeExporting] = useState(null);

  const exportExplode = async (fmt) => {
    setExplodeExporting(fmt);
    try {
      const res = await api.get(`/production-plans/${id}/explode-export`, { params: { format: fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `exploded-requirement-${planCode(plan) || id}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exploded requirement ${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
    finally { setExplodeExporting(null); }
  };

  useEffect(() => {
    if (tab === "subassemblies" && !subAssy) {
      api.get(`/production-plans/${id}/sub-assemblies`).then((r) => setSubAssy(r.data.subAssemblies || [])).catch(() => setSubAssy([]));
    }
    if (tab === "explode" && !explosion) {
      api.get(`/production-plans/${id}/explode`).then((r) => setExplosion(r.data)).catch((e) => setExplosion({ error: apiErrorMessage(e, "Could not explode plan") }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const { data: invData } = useResource("/inventory");
  const { data: company } = useResource("/settings/company");
  const { data: fgProdData } = useResource("/finished-goods/products");
  const { data: ingData } = useResource("/ingredients");
  const { data: prodData } = useResource("/products");

  const inventory = useMemo(() => (Array.isArray(invData) ? invData : []), [invData]);
  const ingredients = useMemo(() => (Array.isArray(ingData) ? ingData : []), [ingData]);
  const products = useMemo(() => (Array.isArray(prodData) ? prodData : []), [prodData]);

  const invMap = useMemo(() => { const m = {}; inventory.forEach((i) => { m[i.ingredientId] = i; }); return m; }, [inventory]);
  const ingMap = useMemo(() => { const m = {}; ingredients.forEach((i) => { m[i.id] = i; }); return m; }, [ingredients]);
  const prodByIdMap = useMemo(() => { const m = {}; products.forEach((p) => { m[p.productId] = p; }); return m; }, [products]);
  const fgSkuMap = useMemo(() => {
    const m = {};
    (Array.isArray(fgProdData) ? fgProdData : []).forEach((p) => { if (p.productName) m[String(p.productName).trim().toLowerCase()] = p.sku; });
    return m;
  }, [fgProdData]);

  const load = async () => {
    setLoadState({ loading: true, error: null });
    try {
      const res = await api.get(`/production-plans/${id}`);
      setPlan(res.data);
      setLoadState({ loading: false, error: null });
    } catch (e) {
      setLoadState({ loading: false, error: apiErrorMessage(e, "Could not load plan") });
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const lines = useMemo(() => (Array.isArray(plan?.lines) ? plan.lines : []), [plan]);
  useEffect(() => {
    if (plan) {
      const init = {};
      lines.forEach((l) => { if (l.actualQty != null) init[l.productId] = String(l.actualQty); });
      setActuals(init);
      setActualDate(plan.actualDate || new Date().toISOString().slice(0, 10));
      setEarmark(!!plan.stockReserved);
    }
  }, [plan, lines]);

  const tol = Number(company?.costVarianceTolerancePct) || 5;

  const aggregated = useMemo(() => aggregateRequirements(lines), [lines]);
  // Carry a rate per ingredient (from the plan's requirement lines) for the cost estimate.
  const rateMap = useMemo(() => {
    const m = {};
    lines.forEach((ln) => (ln.requirements || []).forEach((r) => {
      if (r.ingredientId && m[r.ingredientId] == null) m[r.ingredientId] = Number(r.rate) || 0;
    }));
    return m;
  }, [lines]);

  const reqRows = useMemo(() => aggregated.map((a) => {
    const inv = invMap[a.ingredientId] || {};
    const ing = ingMap[a.ingredientId] || {};
    const stockUnit = inv.unit || ing.unit || a.unit;         // unit stock/rate are held in
    const current = Number(inv.currentStock) || 0;
    const rsv = Number(inv.reserved) || 0;
    const availStock = Number(inv.available ?? current - rsv) || 0;
    // Show stock/net-need in the recipe (requirement) unit for consistency with the recipe.
    const availableInReqUnit = convertQtyOr(availStock, stockUnit, a.unit);
    const currentInReqUnit = convertQtyOr(current, stockUnit, a.unit);
    const rsvInReqUnit = convertQtyOr(rsv, stockUnit, a.unit);
    const netNeed = Math.max(0, +(a.qty - availableInReqUnit).toFixed(4));
    const packSize = Number(ing.packingSize) || 0;
    const packUnit = ing.packingUnit || stockUnit || a.unit;
    // Convert net need (recipe unit) into the packing unit before sizing packs.
    const netNeedInPackUnit = convertQtyOr(netNeed, a.unit, packUnit);
    const packs = packSize > 0 ? Math.ceil(netNeedInPackUnit / packSize) : 0;
    const purchase = packSize > 0 ? +(packs * packSize).toFixed(4) : netNeed;
    const rate = rateMap[a.ingredientId] || Number(ing.rate) || 0;
    // Rate is per stock unit → convert requirement to stock unit for costing.
    const qtyInStockUnit = convertQtyOr(a.qty, a.unit, stockUnit);
    return {
      ...a, requiredQty: a.qty, current: currentInReqUnit, reserved: rsvInReqUnit, available: availableInReqUnit, netNeed,
      packSize, packUnit, packs, purchase, rate,
      estCost: +(qtyInStockUnit * rate).toFixed(2),
    };
  }), [aggregated, invMap, ingMap, rateMap]);

  const estCostTotal = useMemo(() => +reqRows.reduce((s, r) => s + r.estCost, 0).toFixed(2), [reqRows]);
  const anyShort = reqRows.some((r) => Math.max(0, +(r.requiredQty - r.available).toFixed(4)) > 0);

  if (loadState.loading) return <div className="p-6"><LoadingState label="Loading production plan…" /></div>;
  if (loadState.error || !plan) return <div className="p-6"><ErrorState message={loadState.error || "Plan not found"} onRetry={load} /></div>;

  const status = String(plan.status || "Draft");
  const reserved = !!plan.stockReserved;

  const invalidate = () => load();

  const run = async (key) => {
    setBusy(true);
    try {
      if (key === "reserve") {
        const short = reqRows.filter((r) => r.requiredQty > r.current);
        if (short.length > 0 && !(isSuperAdmin && override)) {
          const ls = short.map((r) => `${r.name}: need ${fmtNumber(r.requiredQty, 3)} ${r.unit}, available ${fmtNumber(r.available, 3)}`);
          throw new Error(`Insufficient stock to reserve:\n${ls.join("\n")}${isSuperAdmin ? "\n\nTick Super-Admin override (with reason) to force." : ""}`);
        }
        if (short.length > 0 && isSuperAdmin && override && !overrideReason.trim()) {
          throw new Error("Enter an override reason for the audit log.");
        }
        await api.post(`/production-plans/${plan.id}/reserve`, {
          items: aggregated.map((a) => ({ ingredientId: a.ingredientId, name: a.name, qty: a.qty, unit: a.unit })),
          override: short.length > 0 && override ? true : undefined,
          overrideReason: short.length > 0 && override ? overrideReason.trim() : undefined,
          overrideBy: short.length > 0 && override ? role : undefined,
        });
        setOverride(false); setOverrideReason("");
        toast.success(short.length > 0 && override ? "Reserved with Super-Admin override (audited)" : "Ingredients reserved");
      } else if (key === "release") {
        await api.post(`/production-plans/${plan.id}/release`, {});
        toast.success("Reservation released");
      } else if (key === "start") {
        await api.put(`/production-plans/${plan.id}`, { status: "In Progress" });
        toast.success("Production started");
      } else if (key === "produce") {
        const today = new Date().toISOString().slice(0, 10);
        const producedLines = [];
        const consumedMap = new Map();
        for (const ln of lines) {
          const rawActual = actuals[ln.productId];
          const qty = rawActual === "" || rawActual == null ? Number(ln.plannedQty) || 0 : Number(rawActual);
          if (!(qty > 0)) throw new Error(`Enter the actual produced quantity for ${ln.productName} first.`);
          const sku = ln.sku || fgSkuMap[String(ln.productName || "").trim().toLowerCase()];
          if (!sku) throw new Error(`No Finished-Goods SKU found for ${ln.productName}.`);
          const planned = Number(ln.plannedQty) || 0;
          const scale = planned > 0 ? qty / planned : 1;
          (ln.requirements || []).forEach((r) => {
            if (!r.ingredientId) return;
            const c = +((Number(r.requiredQty) || 0) * scale).toFixed(4);
            const cur = consumedMap.get(r.ingredientId) || { ingredientId: r.ingredientId, name: r.name, unit: r.unit, qty: 0 };
            cur.qty = +(cur.qty + c).toFixed(4);
            consumedMap.set(r.ingredientId, cur);
          });
          producedLines.push({ ...ln, actualQty: qty, sku });
        }
        const consumed = Array.from(consumedMap.values());
        const short = consumed.filter((c) => c.qty > (Number(invMap[c.ingredientId]?.currentStock) || 0) + 1e-6);
        if (short.length > 0 && !(isSuperAdmin && override)) {
          const l = short.map((c) => `${c.name}: need ${fmtNumber(c.qty, 3)} ${c.unit}, in stock ${fmtNumber(Number(invMap[c.ingredientId]?.currentStock) || 0, 3)}`);
          throw new Error(`Not enough raw material to produce:\n${l.join("\n")}${isSuperAdmin ? "\n\nTick Super-Admin override (with reason) to force." : ""}`);
        }
        for (const ln of producedLines) {
          const meta = prodMeta[ln.productId] || {};
          await api.post("/finished-goods/produce", {
            sku: ln.sku, qty: ln.actualQty, productName: ln.productName, unit: ln.unit,
            planId: plan.id, productId: ln.productId,
            batchNo: meta.batchNo || "", mfgDate: meta.mfgDate || today, expiryDate: meta.expiryDate || "",
          });
        }
        for (const c of consumed) {
          await api.post("/inventory/transactions", {
            ingredientId: c.ingredientId, type: "production_issue", qty: c.qty,
            reference: plan.id, referenceType: "production", remarks: `Produced ${planCode(plan)}`,
          });
        }
        if (reserved) { try { await api.post(`/production-plans/${plan.id}/release`, {}); } catch (e) { /* non-fatal */ } }
        await api.put(`/production-plans/${plan.id}`, {
          date: plan.date, section: plan.section, status: "Completed",
          lines: producedLines, issued: plan.issued || {}, actualDate: today,
        });
        toast.success("Produced — Finished Goods received and raw materials consumed via the ledger");
      }
      invalidate();
    } catch (e) {
      toast.error(e?.response ? apiErrorMessage(e, "Action failed") : (e?.message || "Action failed"));
    } finally { setBusy(false); setPending(null); }
  };

  const printRequirement = () => {
    const html = buildRequirementHtml(plan, company || {}, reqRows, anyShort ? "SHORT" : "READY");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    w.document.write(html); w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 350);
  };

  const emailRequirement = async () => {
    const html = buildRequirementHtml(plan, company || {}, reqRows, anyShort ? "SHORT" : "READY");
    try {
      await api.post("/reports/email", {
        reportType: "Production Requirement", subject: `Production Requirement ${planCode(plan)} · ${plan.date}`,
        filename: `${planCode(plan)}-requirement.pdf`, title: "Production Requirement",
        subtitle: `${planCode(plan)} · ${plan.date}`, html,
      });
      toast.success("Requirement emailed");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Email failed"));
    }
  };

  const saveActual = async () => {
    setSavingActual(true);
    const updatedLines = lines.map((l) => ({
      ...l,
      actualQty: actuals[l.productId] === "" || actuals[l.productId] == null ? (l.actualQty ?? null) : Number(actuals[l.productId]),
    }));
    try {
      await api.put(`/production-plans/${plan.id}`, {
        date: plan.date, section: plan.section, status: plan.status,
        lines: updatedLines, issued: plan.issued || {}, actualDate,
      });
      toast.success("Actual production saved");
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save actual production"));
    } finally { setSavingActual(false); }
  };

  const removeLine = async (ln) => {
    if (!["Draft", "Planned"].includes(status)) { toast.error("Only Draft/Planned plans can remove lines"); return; }
    try {
      await api.put(`/production-plans/${plan.id}`, {
        date: plan.date, section: plan.section, status: plan.status,
        lines: lines.filter((l) => l !== ln), issued: plan.issued || {},
      });
      toast.success("Product removed from plan");
      invalidate();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not remove line")); }
  };

  const varianceStatus = (planned, actual) => {
    if (actual == null || actual === "") return { label: "Pending", tone: "text-stone-400" };
    const pl = Number(planned) || 0; const ac = Number(actual) || 0;
    const pct = pl ? ((ac - pl) / pl) * 100 : 0;
    if (Math.abs(pct) <= tol) return { label: "On Plan", tone: "text-emerald-600" };
    return pct > 0 ? { label: "Above Plan", tone: "text-sky-600" } : { label: "Below Plan", tone: "text-rose-600" };
  };

  const filteredLines = lines.filter((ln) =>
    !prodSearch || String(ln.productName || "").toLowerCase().includes(prodSearch.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="production-plan-detail-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 border-b border-stone-200 bg-stone-50/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3.5">
          <div className="flex items-center gap-3">
            <button data-testid="plan-detail-back" onClick={() => navigate("/production-plans")}
              className="rounded-lg p-1.5 text-stone-500 hover:bg-stone-200/60 hover:text-stone-800">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-display text-lg font-bold text-stone-900">PRODUCTION PLAN</h1>
              <p className="text-xs text-stone-500">{planCode(plan)} · {productTypeLabel(plan.section)} · {fmtDate(plan.date)}</p>
            </div>
          </div>
          <StatusBadge value={status} />
        </div>
        {/* Black pill tabs */}
        <div className="mx-auto flex max-w-6xl gap-2 px-6 pb-3">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`plan-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
                tab === t.key ? "bg-stone-900 text-white" : "border border-stone-300 bg-white text-stone-600 hover:border-stone-400"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-6">
        {/* ---------------- PRODUCTS ---------------- */}
        {tab === "products" && (
          <div className="space-y-4" data-testid="plan-tab-products-panel">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <Input data-testid="plan-product-search" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)}
                placeholder="Search product (e.g. choc)" className="h-11 rounded-xl border-stone-300 bg-white pl-9 text-sm" />
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Products · {filteredLines.length}</div>
            {filteredLines.length === 0 ? (
              <p className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-muted-foreground">This plan has no product lines.</p>
            ) : (
              <div className="space-y-3">
                {filteredLines.map((ln, i) => {
                  const prod = prodByIdMap[ln.productId];
                  const labelsPending = prod && !(prod.internalLabel && prod.outerCartonLabel);
                  return (
                    <div key={i} data-testid={`plan-product-${i}`} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                      <div>
                        <div className="font-display text-sm font-bold text-stone-900">{ln.productName || ln.sku}</div>
                        <div className="mt-0.5 text-xs text-stone-500">
                          {fmtNumber(ln.plannedQty)} {ln.unit} · recipe yield {fmtNumber(ln.yieldQty)} {ln.yieldUnit} · V{String(ln.recipeVersion).replace(/^v/i, "")}
                        </div>
                        {labelsPending && (
                          <span data-testid={`plan-product-labels-pending-${i}`} className="mt-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-[10px] font-semibold text-amber-800">
                            <AlertTriangle className="h-3 w-3" /> LABELS PENDING
                          </span>
                        )}
                      </div>
                      <button data-testid={`plan-product-remove-${i}`} onClick={() => removeLine(ln)}
                        disabled={!["Draft", "Planned"].includes(status)}
                        className="rounded-lg p-2 text-stone-300 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30" title="Remove from plan">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ---------------- REQUIREMENT ---------------- */}
        {tab === "requirement" && (
          <div className="space-y-4" data-testid="plan-tab-requirement-panel">
            {/* Reserve control */}
            <div className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm">
              <label className="flex items-center gap-2 text-xs text-stone-600" data-testid="plan-earmark-toggle">
                <Checkbox checked={earmark} onCheckedChange={(v) => setEarmark(!!v)} />
                Earmark ingredients so they aren't double-booked.
              </label>
              <div className="flex gap-2">
                {reserved && (
                  <Button data-testid="plan-release-btn" size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => setPending("release")}>
                    <Unlock className="h-3.5 w-3.5" /> Release
                  </Button>
                )}
                <Button data-testid="plan-reserve-btn" size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                  disabled={busy || aggregated.length === 0} onClick={() => setPending("reserve")}>
                  <Lock className="h-3.5 w-3.5" /> Reserve
                </Button>
              </div>
            </div>

            {/* Estimated raw material cost */}
            <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-5 py-4" data-testid="plan-est-cost">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Estimated Raw Material Cost</div>
                <div className="font-mono text-2xl font-bold text-amber-800">{fmtCurrency(estCostTotal)}</div>
              </div>
              <Tag className="h-5 w-5 text-amber-500" />
            </div>

            <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Ingredient · Theoretical → Indent / Purchase</div>

            {reqRows.length === 0 ? (
              <p className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-muted-foreground">No requirement lines. Recalculate requirements from the plan list.</p>
            ) : (
              <div className="space-y-3">
                {reqRows.map((r) => (
                  <div key={r.ingredientId} data-testid={`plan-req-card-${r.ingredientId}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-sm font-bold uppercase text-stone-900">{r.name}</div>
                        <div className="mt-0.5 text-xs text-stone-500">Theoretical {fmtNumber(r.requiredQty, 3)} {r.unit} · ≈ {fmtCurrency(r.estCost)}</div>
                      </div>
                      <div className="w-28">
                        <div className="mb-1 text-right text-[10px] font-semibold uppercase tracking-wider text-stone-400">Issued</div>
                        <Input data-testid={`plan-issued-${r.ingredientId}`} type="number" min="0" placeholder="0"
                          defaultValue={plan.issued?.[r.ingredientId] ?? ""} className="h-9 bg-stone-50 text-right font-mono text-sm" readOnly />
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <StatBox label="In Stock" value={`${fmtNumber(r.current, 3)} ${r.unit}`} testId={`plan-req-instock-${r.ingredientId}`} />
                      <StatBox label="Net Need" value={`${fmtNumber(r.netNeed, 3)} ${r.unit}`} tone={r.netNeed > 0 ? "text-rose-600" : "text-emerald-600"} testId={`plan-req-netneed-${r.ingredientId}`} />
                      <StatBox label="Packs" value={r.packSize > 0 ? `${r.packs} × ${fmtNumber(r.packSize, 3)} ${r.packUnit}` : "—"} testId={`plan-req-packs-${r.ingredientId}`} />
                      <StatBox label="Purchase" value={r.netNeed > 0 ? `${fmtNumber(r.purchase, 3)} ${r.packSize > 0 ? r.packUnit : r.unit}` : "0"} tone={r.netNeed > 0 ? "text-amber-700" : "text-emerald-600"} testId={`plan-req-purchase-${r.ingredientId}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {anyShort && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700" data-testid="plan-reserve-shortage">
                <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Reservation exceeds available stock — blocked to prevent negative inventory.</div>
                {isSuperAdmin && (
                  <div className="mt-2 space-y-1.5">
                    <label className="flex items-center gap-2" data-testid="plan-override-toggle">
                      <Checkbox checked={override} onCheckedChange={(v) => setOverride(!!v)} /> Super-Admin override (force reserve despite shortage)
                    </label>
                    {override && <Input data-testid="plan-override-reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Override reason (audited)" className="h-8 bg-white text-xs" />}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---------------- SUB-ASSEMBLIES ---------------- */}
        {tab === "subassemblies" && <SubAssembliesPanel subAssy={subAssy} />}

        {/* ---------------- EXPLODE TO RAW MATERIALS ---------------- */}
        {tab === "explode" && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-stone-500">Printable exploded raw-material requirement for purchase planning (stock-aware).</p>
              <div className="flex items-center gap-2">
                <Button data-testid="plan-explode-export-pdf" size="sm" variant="outline" className="gap-1.5" disabled={!!explodeExporting} onClick={() => exportExplode("pdf")}>
                  {explodeExporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Export PDF
                </Button>
                <Button data-testid="plan-explode-export-xlsx" size="sm" variant="outline" className="gap-1.5" disabled={!!explodeExporting} onClick={() => exportExplode("xlsx")}>
                  {explodeExporting === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Export Excel
                </Button>
              </div>
            </div>
            <ExplodePanel explosion={explosion} />
          </div>
        )}

        {/* ---------------- PRODUCTION ---------------- */}
        {tab === "production" && (          <div className="space-y-4" data-testid="plan-tab-production-panel">
            <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-stone-800">
                <ClipboardList className="h-4 w-4 text-amber-600" /> End-of-Day Actual Production
              </div>
              <div className="mb-3 flex items-center gap-2 text-xs">
                <span className="text-stone-500">Actual date</span>
                <Input data-testid="plan-actual-date" type="date" value={actualDate} onChange={(e) => setActualDate(e.target.value)} className="h-8 w-40 text-xs" />
              </div>
              <div className="space-y-2">
                {lines.map((ln) => {
                  const actual = actuals[ln.productId];
                  const vs = varianceStatus(ln.plannedQty, actual);
                  const varQty = actual === "" || actual == null ? null : Number(actual) - Number(ln.plannedQty || 0);
                  const varPct = actual && Number(ln.plannedQty) ? (((Number(actual) - Number(ln.plannedQty)) / Number(ln.plannedQty)) * 100) : null;
                  return (
                    <div key={ln.productId} className="grid grid-cols-12 items-center gap-2 rounded-lg bg-stone-50/60 px-2 py-1.5 text-xs">
                      <span className="col-span-4 font-medium text-stone-700">{ln.productName}</span>
                      <span className="col-span-2 text-right text-stone-500">Plan {fmtNumber(ln.plannedQty)}</span>
                      <Input data-testid={`plan-actual-${ln.productId}`} type="number" min="0" placeholder="Actual"
                        value={actual ?? ""} onChange={(e) => setActuals((a) => ({ ...a, [ln.productId]: e.target.value }))}
                        className="col-span-2 h-8 bg-white text-xs" />
                      <span className="col-span-2 text-right font-mono">{varQty == null ? "—" : `${varQty > 0 ? "+" : ""}${fmtNumber(varQty)} (${varPct > 0 ? "+" : ""}${fmtNumber(varPct, 1)}%)`}</span>
                      <span className={`col-span-2 text-right font-semibold ${vs.tone}`}>{vs.label}</span>
                    </div>
                  );
                })}
              </div>
              <Button data-testid="plan-save-actual" size="sm" className="mt-3 gap-1.5 bg-emerald-700 hover:bg-emerald-800" disabled={savingActual} onClick={saveActual}>
                {savingActual ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save Actual Production
              </Button>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-stone-700">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Workflow — each step confirms before writing
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="plan-action-start" size="sm" variant="outline" className="gap-1.5" disabled={busy || lines.length === 0 || status === "In Progress" || status === "Completed"} onClick={() => setPending("start")}>
                  <Play className="h-3.5 w-3.5" /> Start production run
                </Button>
                <Button data-testid="plan-action-produce" size="sm" className="gap-1.5 bg-amber-700 hover:bg-amber-800" disabled={busy || lines.length === 0 || status === "Completed" || status === "Draft"} onClick={() => setPending("produce")}>
                  <PackagePlus className="h-3.5 w-3.5" /> Produce → Finished Goods
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-stone-400">Produce consumes raw materials, creates the batch (cost locked) and receives Finished Goods in one ledger-backed step. Enter Actual Production above before producing.</p>
            </div>
          </div>
        )}

        {/* ---------------- REPORT ---------------- */}
        {tab === "report" && (
          <div className="space-y-4" data-testid="plan-tab-report-panel">
            <div className="flex flex-wrap gap-2">
              <Button data-testid="plan-print-req" size="sm" className="gap-1.5 bg-stone-800 hover:bg-stone-900" onClick={printRequirement}>
                <Printer className="h-3.5 w-3.5" /> Print Requirement
              </Button>
              <Button data-testid="plan-email-req" size="sm" variant="outline" className="gap-1.5" onClick={emailRequirement}>
                <Mail className="h-3.5 w-3.5" /> Email Requirement
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
              <div className="border-b border-stone-200 bg-stone-50 px-4 py-2 text-sm font-semibold text-stone-800">Planned vs Actual</div>
              <table className="w-full text-sm">
                <thead className="bg-stone-50/60 text-left text-[10px] uppercase text-stone-500">
                  <tr><th className="px-4 py-2">Product</th><th className="px-4 py-2 text-right">Planned</th><th className="px-4 py-2 text-right">Actual</th><th className="px-4 py-2 text-right">Variance</th><th className="px-4 py-2 text-right">Status</th></tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {lines.map((ln) => {
                    const actual = ln.actualQty ?? actuals[ln.productId];
                    const vs = varianceStatus(ln.plannedQty, actual);
                    return (
                      <tr key={ln.productId} data-testid={`plan-report-${ln.productId}`}>
                        <td className="px-4 py-2.5 text-stone-800">{ln.productName}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{fmtNumber(ln.plannedQty)} {ln.unit}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{actual == null ? "—" : `${fmtNumber(actual)} ${ln.unit}`}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{actual == null ? "—" : fmtNumber(Number(actual) - Number(ln.plannedQty || 0))}</td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${vs.tone}`}>{vs.label}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Confirm dialog for reserve/release/start/produce */}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Confirm {pending}</AlertDialogTitle>
            <AlertDialogDescription>
              {pending === "reserve" && "Reserve the calculated ingredient quantities against raw-material inventory."}
              {pending === "release" && "Release the reserved ingredients back to available stock."}
              {pending === "start" && "Mark this plan In Progress."}
              {pending === "produce" && "Record production: consume raw materials, create the batch, and receive Finished Goods via the ledger."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pending === "produce" && (
            <div className="max-h-60 space-y-2 overflow-auto rounded-lg bg-stone-50/70 p-2" data-testid="produce-batch-capture">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Batch details (auto-numbered if left blank)</div>
              {lines.map((ln) => {
                const m = prodMeta[ln.productId] || {};
                const set = (k, v) => setProdMeta((p) => ({ ...p, [ln.productId]: { ...(p[ln.productId] || {}), [k]: v } }));
                return (
                  <div key={ln.productId} className="grid grid-cols-4 items-center gap-2 text-xs">
                    <div className="truncate font-medium text-stone-700">{ln.productName}</div>
                    <input data-testid={`produce-batch-${ln.productId}`} placeholder="Batch # (auto)" value={m.batchNo || ""} onChange={(e) => set("batchNo", e.target.value)} className="h-8 rounded-md border border-stone-200 px-2" />
                    <input type="date" data-testid={`produce-mfg-${ln.productId}`} value={m.mfgDate || new Date().toISOString().slice(0, 10)} onChange={(e) => set("mfgDate", e.target.value)} className="h-8 rounded-md border border-stone-200 px-2" />
                    <input type="date" data-testid={`produce-expiry-${ln.productId}`} value={m.expiryDate || ""} onChange={(e) => set("expiryDate", e.target.value)} className="h-8 rounded-md border border-stone-200 px-2" />
                  </div>
                );
              })}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plan-action-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="plan-action-confirm" className="bg-amber-700 hover:bg-amber-800" onClick={() => run(pending)}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, proceed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
